import _ from 'lodash';

// These are also roughly organized by how likely someone is to use Apple Wallet here.
const keyToDescriptionMap: Record<string, string> = {
  allPurchases: 'All other purchases',
  'allPurchases.mobileWallet': 'Mobile wallet purchases',
  'allPurchases.mobileWallet.applePay': 'Apple Pay purchases',
  allForeignPurchases: 'Foreign purchases',
  foreignTransactions: 'Foreign transactions',
  dining: 'Dining',
  gas: 'Gas',
  'gas.cenex': 'Cenex',
  'gas.costco': 'Costco gas',
  'gas.evCharging': 'EV charging',
  grocery: 'Grocery',
  streaming: 'Streaming',
  drugstore: 'Drugstore',
  'drugstore.cvs': 'CVS',
  drugstores: 'Drugstores',
  fitness: 'Fitness',
  'streaming.disneyplus': 'Disney+',
  'dining.takeoutDelivery': 'Takeout/delivery',
  'dining.takeoutDelivery.ubereats': 'UberEats',
  'dining.takeoutDelivery.doordash': 'Doordash',
  officeSupplies: 'Office supplies',
  internetCablePhone: 'Internet, cable & phone',
  travel: 'Travel',
  'travel.hotel.general': 'Hotel stays',
  'travel.hotel.bestWestern': 'Best Western hotel stays',
  'travel.hotel.choice': 'Choice hotel stays',
  'travel.hotel.hyatt': 'Hyatt hotel stays',
  'travel.hotel.ihg': 'IHG hotel stays',
  'travel.hotel.wyndham': 'Wyndham hotel stays',
  'travel.hotel.marriott_bonvoy': 'Marriott Bonvoy hotel stays',
  'travel.hotel.marriott': 'Marriott hotel stays',
  'travel.hotel.choiceHotels': 'Choice Hotels stays',
  'travel.hotel.hilton': 'Hilton hotel stays',
  'travel.transit': 'Transit',
  'travel.rideshare.lyft': 'Lyft',
  'travel.rideshare.uber': 'Uber',
  'travel.travelportal': 'Travel portal',
  'travel.airline': 'Airlines',
  'travel.airline.united': 'United flights',
  'travel.airline.delta': 'Delta flights',
  'travel.airline.american': 'American flights',
  'travel.airline.southwest': 'Southwest flights',
  'travel.airline.alaska': 'Alaska flights',
  'travel.airline.hawaiian': 'Hawaiian airlines flights',
  'travel.airline.frontier': 'Frontier flights',
  'travel.airline.aerLingus': 'Aer Lingus flights',
  'travel.airline.jetblue': 'JetBlue flights',
  'travel.airline.britishAirways': 'British Airways flights',
  'travel.airline.airFranceKlmFlyingBlue': 'Air France-KLM flights',
  'travel.airline.airCanadaAeroplan': 'Air Canada flights',
  'travel.airline.skyteamAirlines': 'SkyTeam airlines flights',
  'travel.airline.breeze': 'Breeze Airways flights',
  'travel.airline.iberia': 'Iberia flights',
  'travel.airline.level': 'LEVEL flights',
  'travel.airline.spirit': 'Spirit Airlines flights',
  'travel.airline.ana': 'ANA flights',
  'travel.airline.avianca': 'Avianca flights',
  'travel.airline.cathayPacific': 'Cathay Pacific flights',
  'travel.airline.emirates': 'Emirates flights',
  'travel.airline.korean': 'Korean Air flights',
  'travel.airline.latam': 'LATAM flights',
  'travel.airline.lufthansa': 'Lufthansa flights',
  'travel.airline.virgin': 'Virgin Airlines flights',
  'travel.airline.airDolomiti': 'Air Dolomiti flights',
  'travel.airline.austrianAirlines': 'Austrian Airlines flights',
  'travel.airline.brusselsAirlines': 'Brussels Airlines flights',
  'travel.airline.croatiaAirlines': 'Croatia Airlines flights',
  'travel.airline.eurowings': 'Eurowings flights',
  'travel.airline.lotPolishAirlines': 'LOT Polish Airlines flights',
  'travel.airline.luxair': 'Luxair flights',
  'travel.airline.swissInternationalAirLines':
    'Swiss International Air Lines flights',
  'travel.airline.allegiant': 'Allegiant flights',
  'travel.rideshare': 'Rideshare',
  'travel.hotel': 'Hotel stays',
  'travel.cruise': 'Cruise',
  'travel.cruise.royalCaribbean': 'Royal Caribbean cruise',
  'travel.cruise.carnival': 'Carnival cruise',
  'travel.cruise.norwegian': 'Norwegian cruise',
  'travel.rentalCar': 'Car rentals',

  // Common categories
  homeImprovement: 'Home improvement',
  'homeImprovement.aceHardware': 'Ace Hardware',
  'homeImprovement.lowes': "Lowe's",
  'homeImprovement.homeDepot': 'Home Depot',
  'homeImprovement.menards': 'Menards',
  wholesaleClub: 'Wholesale clubs',
  selfCare: 'Self care',
  'selfCare.cosmeticBarberHairSalons': 'Cosmetic, barber & hair salons',
  homeFurnishings: 'Home furnishings',
  'homeFurnishings.ikea': 'IKEA',
  'homeFurnishings.potteryBarn': 'Pottery Barn',
  'homeFurnishings.williamsSonoma': 'Williams Sonoma',
  'homeFurnishings.restorationHardware': 'Restoration Hardware',
  'homeFurnishings.crateAndBarrel': 'Crate & Barrel',
  electronics: 'Electronics',
  'electronics.apple': 'Apple',
  'electronics.bestBuy': 'Best Buy',
  telecommunications: 'Telecommunications',
  'telecommunications.verizon': 'Verizon',
  entertainment: 'Entertainment',
  'entertainment.disneyPlusHuluEspn': 'Disney+, Hulu & ESPN+',
  'entertainment.liveEntertainment': 'Live entertainment',
  'entertainment.disney': 'Disney',
  'entertainment.disney_us_locations': 'Disney US locations',
  petPurchases: 'Pet purchases',
  'petPurchases.chewy': 'Chewy',
  onlineShopping: 'Online shopping',
  'onlineShopping.amazon': 'Amazon',
  'onlineShopping.paypal': 'PayPal',
  'onlineShopping.wayfair': 'Wayfair',
  'onlineShopping.gapInc': 'Gap Inc',
  departmentStores: 'Department stores',
  'departmentStores.macys': "Macy's",
  'departmentStores.nordstrom': 'Nordstrom',
  'departmentStores.kohls': "Kohl's",
  'departmentStores.dillards': "Dillard's",
  sportsEquipment: 'Sports equipment',
  'sportsEquipment.llBean': 'L.L.Bean',
  'sportsEquipment.reiCoop': 'REI Co-op',
  'sportingGoods.bassProAndCabelas': "Bass Pro Shops & Cabela's",
  recreationLeisure: 'Recreation & leisure',
  craftsHobbies: 'Crafts & hobbies',
  gardeningFloral: 'Gardening & floral',
  bookstores: 'Bookstores',
  billsAndUtilities: 'Bills & utilities',
  phonePlans: 'Phone plans',
  computerServices: 'Computer services',
  businessConsultingServices: 'Business consulting services',
  onlineAdvertising: 'Online advertising',
  shipping: 'Shipping',
  charity: 'Charity',
  automotive: 'Auto parts & service',
  medicalExpenses: 'Medical expenses',

  // Brand-specific fallbacks (will show as "Store name")
  'drugstore.walgreens': 'Walgreens',
  ihg_hotels: 'IHG Hotels',
  whole_foods: 'Whole Foods',
  costco: 'Costco',
  walmart: 'Walmart',
  target: 'Target',
  bedBathBeyond: 'Bed Bath & Beyond',
  microsoftStore: 'Microsoft Store',
  digitalMicrosoftStore: 'Digital Microsoft Store',
  qvc: 'QVC',
  anthropologie: 'Anthropologie',
  barnesAndNoble: 'Barnes & Noble',
  lululemon: 'Lululemon',
  capitalVacations: 'Capital Vacations',
  rci: 'RCI',
  paperSource: 'Paper Source',
  paper_source: 'Paper Source',
  banana_republic: 'Banana Republic',
  rotating_quarterly_rewards: 'Rotating quarterly rewards',
  rent: 'Rent',
  housing: 'Housing',
  gm_purchases: 'GM purchases',
  hulu: 'Hulu',
  expedia: 'Expedia',
  hotels_com: 'Hotels.com',
  vrbo: 'VRBO',
  breeze_bundles_and_trip_add_ons: 'Breeze bundles and trip add-ons',
  booking_com: 'Booking.com',
  exxon: 'Exxon',
  mobil: 'Mobil',
  duane_reade: 'Duane Reade',
  adobe: 'Adobe',
  microsoft_365: 'Microsoft 365',
  google_workspace: 'Google Workspace',
  salesforce: 'Salesforce',
  zoom: 'Zoom',
  slack: 'Slack',
  dropbox: 'Dropbox',
  box: 'Box',
  instacart: 'Instacart',
  emirates_airlines: 'Emirates Airlines',
  gap: 'Gap',
  old_navy: 'Old Navy',
  athleta: 'Athleta',
  thriftStores: 'Thrift stores',
};

const missingKeys = new Set<string>();

export function formatSpendCategory(key: string): string {
  if (!keyToDescriptionMap[key]) {
    if (!missingKeys.has(key)) {
      missingKeys.add(key);
      console.log(
        'Missing category names:',
        Array.from(missingKeys).join(', ')
      );
    }
  }
  return keyToDescriptionMap[key] || _.capitalize(key);
}
